import type {
  ClientMessage,
  ClientMessageType,
  ServerMessage,
  ServerMessageType,
} from './types';
import { saveLastEventId } from './identity';

export type ConnectionStatus = 'idle' | 'connecting' | 'open' | 'reconnecting' | 'closed';

export interface WsClientOptions {
  url: string;
  token: string;
  /** v1.1：房间号（4 位数字），必填；为空时 wsClient 不会主动连接 */
  roomCode: string;
  getLastEventId: () => number;
  onMessage: (msg: ServerMessage) => void;
  onStatusChange: (status: ConnectionStatus) => void;
  /** v1.1：房间相关致命错误（INVALID_ROOM / ROOM_NOT_FOUND / ROOM_FULL / ROOM_CLOSED 等）回调 */
  onRoomFatal?: (info: { code: string; closeCode: number; reason?: string }) => void;
  /** 5s 超过 → 仍未连上视为 "已离场"，UI 标红 */
  reconnectBudgetMs?: number;
  /** 心跳间隔 (SDD §0 心跳 25s) */
  pingIntervalMs?: number;
  /** 测试注入 socket */
  socketFactory?: (url: string) => WebSocketLike;
  /** 测试用 — 自定义计时器 */
  timer?: TimerLike;
}

/** WS close code → 业务 code 映射（SDD-WS §0 + PRD §B.4） */
const ROOM_FATAL_CLOSE_CODES: Record<number, string> = {
  4400: 'INVALID_ROOM',
  4404: 'ROOM_NOT_FOUND',
  4429: 'ROOM_FULL',
  4410: 'ROOM_CLOSED',
};

export interface WebSocketLike {
  readyState: number;
  send(data: string): void;
  close(code?: number, reason?: string): void;
  addEventListener(type: 'open' | 'close' | 'message' | 'error', cb: (ev: unknown) => void): void;
}

export interface TimerLike {
  setTimeout(fn: () => void, ms: number): unknown;
  clearTimeout(id: unknown): void;
  setInterval(fn: () => void, ms: number): unknown;
  clearInterval(id: unknown): void;
  now(): number;
}

const defaultTimer: TimerLike = {
  setTimeout: (fn, ms) => globalThis.setTimeout(fn, ms),
  clearTimeout: (id) => globalThis.clearTimeout(id as ReturnType<typeof setTimeout>),
  setInterval: (fn, ms) => globalThis.setInterval(fn, ms),
  clearInterval: (id) => globalThis.clearInterval(id as ReturnType<typeof setInterval>),
  now: () => Date.now(),
};

const RECONNECT_BACKOFF_MS = [200, 500, 1000, 2000, 5000];

let reqSeq = 0;
export function nextReqId(): string {
  reqSeq += 1;
  return `c-${Date.now()}-${reqSeq}`;
}

export class WsClient {
  private ws: WebSocketLike | null = null;
  private status: ConnectionStatus = 'idle';
  private reconnectAttempts = 0;
  private reconnectTimer: unknown = null;
  private pingTimer: unknown = null;
  private firstDisconnectAt: number | null = null;
  private manuallyClosed = false;
  private timer: TimerLike;

  constructor(private readonly opts: WsClientOptions) {
    this.timer = opts.timer ?? defaultTimer;
  }

  // ───────── public ─────────

  connect(): void {
    this.manuallyClosed = false;
    this.openSocket(false);
  }

  send<T = unknown>(type: ClientMessageType, payload: T, reqId?: string): string {
    const id = reqId ?? nextReqId();
    const frame: ClientMessage<T> = {
      type,
      reqId: id,
      ts: this.timer.now(),
      payload,
    };
    if (this.ws && this.ws.readyState === 1) {
      this.ws.send(JSON.stringify(frame));
    }
    return id;
  }

  close(): void {
    this.manuallyClosed = true;
    this.clearPing();
    this.clearReconnect();
    if (this.ws) {
      try {
        this.ws.close(1000, 'manual');
      } catch {
        // ignore
      }
      this.ws = null;
    }
    this.setStatus('closed');
  }

  getStatus(): ConnectionStatus {
    return this.status;
  }

  /** 测试可见：当前是否在 5s 重连预算内 */
  isWithinReconnectBudget(): boolean {
    if (this.firstDisconnectAt == null) return true;
    const budget = this.opts.reconnectBudgetMs ?? 5000;
    return this.timer.now() - this.firstDisconnectAt <= budget;
  }

  // ───────── internal ─────────

  private openSocket(isReconnect: boolean): void {
    this.setStatus(isReconnect ? 'reconnecting' : 'connecting');
    const last = this.opts.getLastEventId();
    // v1.1 协议（SDD-WS §0 + §8.1）：URL 必须带 room= + proto=v1
    const url =
      `${this.opts.url}?room=${encodeURIComponent(this.opts.roomCode)}` +
      `&token=${encodeURIComponent(this.opts.token)}` +
      `&lastEventId=${last}&proto=v1`;
    const factory = this.opts.socketFactory ?? ((u) => new WebSocket(u) as unknown as WebSocketLike);
    let socket: WebSocketLike;
    try {
      socket = factory(url);
    } catch {
      this.scheduleReconnect();
      return;
    }
    this.ws = socket;

    socket.addEventListener('open', () => {
      this.reconnectAttempts = 0;
      this.firstDisconnectAt = null;
      this.setStatus('open');
      this.startPing();
    });

    socket.addEventListener('message', (ev) => {
      const data = (ev as MessageEvent).data;
      if (typeof data !== 'string') return;
      let parsed: ServerMessage;
      try {
        parsed = JSON.parse(data);
      } catch {
        return;
      }
      if (typeof parsed.eventId === 'number' && parsed.eventId > 0) {
        saveLastEventId(parsed.eventId);
      }
      this.opts.onMessage(parsed);
    });

    socket.addEventListener('error', () => {
      // 由 close 触发重连
    });

    socket.addEventListener('close', (ev) => {
      this.clearPing();
      this.ws = null;
      if (this.manuallyClosed) return;
      // v1.1：房间相关致命 close code 不重连，回调上层清 localStorage + 跳 /
      const closeCode = (ev as CloseEvent | undefined)?.code ?? 0;
      const reason = (ev as CloseEvent | undefined)?.reason;
      const fatal = ROOM_FATAL_CLOSE_CODES[closeCode];
      if (fatal) {
        this.manuallyClosed = true;
        this.clearReconnect();
        this.setStatus('closed');
        this.opts.onRoomFatal?.({ code: fatal, closeCode, reason });
        return;
      }
      if (this.firstDisconnectAt == null) this.firstDisconnectAt = this.timer.now();
      this.scheduleReconnect();
    });
  }

  private scheduleReconnect(): void {
    this.clearReconnect();
    const delay =
      RECONNECT_BACKOFF_MS[Math.min(this.reconnectAttempts, RECONNECT_BACKOFF_MS.length - 1)];
    this.reconnectAttempts += 1;
    this.setStatus('reconnecting');
    this.reconnectTimer = this.timer.setTimeout(() => {
      this.reconnectTimer = null;
      this.openSocket(true);
    }, delay);
  }

  private clearReconnect(): void {
    if (this.reconnectTimer) {
      this.timer.clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
  }

  private startPing(): void {
    this.clearPing();
    const interval = this.opts.pingIntervalMs ?? 25_000;
    this.pingTimer = this.timer.setInterval(() => {
      this.send('PING', {});
    }, interval);
  }

  private clearPing(): void {
    if (this.pingTimer) {
      this.timer.clearInterval(this.pingTimer);
      this.pingTimer = null;
    }
  }

  private setStatus(s: ConnectionStatus): void {
    if (s === this.status) return;
    this.status = s;
    this.opts.onStatusChange(s);
  }
}

export type { ServerMessageType };
